import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createDenialSupport,
	getAllDenialSupports,
	getDenialSupportProducts,
	getSingleDenialSupport,
} from '@/services/denialSupport';
import type {
	CreateDenialSupportPayload,
	DenialSupportDetailsResponse,
	DenialSupportProductsResponse,
} from '@/types/denialSupport';
import type { FilterType } from '@/types/table';

export const useGetAllDenialSupports = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'denialSupports',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getAllDenialSupports(
				page,
				limit,
				columnsFilters,
				search,
				status as string,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetSingleDenialSupport = (
	id: string | null,
	enabled = true,
) => {
	return useQuery<DenialSupportDetailsResponse>({
		queryKey: ['denialSupport', id],
		queryFn: () => getSingleDenialSupport(id as string),
		enabled: enabled && !!id,
		keepPreviousData: false,
	});
};

export const useCreateDenialSupport = (
	onSuccess?: (data: any) => void,
	onError?: (error: any) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (denialSupportData: CreateDenialSupportPayload) =>
			createDenialSupport(denialSupportData),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['denialSupport'] });
			onSuccess?.(data);
		},
		onError: (error) => {
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to create denial support. Please try again.',
			);
			onError?.(error);
		},
	});
};

export const useGetDenialSupportProducts = () => {
	return useQuery<DenialSupportProductsResponse>({
		queryKey: ['denialSupportProducts'],
		queryFn: () => getDenialSupportProducts(),
		enabled: true,
		keepPreviousData: false,
	});
};